import React, { useState } from 'react';
import CardMCFilter from './CardMCFilter';
import { Button, Icon } from 'semantic-ui-react';
import * as XLSX from 'xlsx';
import CardMCVegaChart from './components/CardMCVegaChart';
import { VisualizationSpec } from 'vega-embed';

type CardMCNewUserProps = { 
  // Add props if needed
  controller: any;
  runSequence: any;
  MeChartSequence: any;
}
// Component: CardMCNewUser
// Purpose: Displays the count of new members (patients) per month or year.
const CardMCNewUser = (props: CardMCNewUserProps) => {
  // State for filtering
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(1);
  const [endYear, setEndYear] = useState(new Date().getFullYear());
  const [endMonth, setEndMonth] = useState(12);

  const thaiMonths = [
    'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
    'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'
  ];

  // View Mode: 'monthly' (default) or '3year'
  const [viewMode, setViewMode] = useState<'monthly' | '3year'>('monthly');

  // Transpose state
  const [isTransposed, setIsTransposed] = useState(false);

  // State for chart data
  const [chartData, setChartData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  // Actions
  const onSearch = () => { fetchData(); };
  const onPrint = () => { window.print(); };

  const onDownload = () => {
    if (!chartData || chartData.length === 0) {
      alert('No data to download');
      return;
    }

    let dataToExport = chartData.map(item => ({
      [viewMode === 'monthly' ? "Month" : "Year"]: viewMode === 'monthly' ? item.month_label : item.year,
      "New Members": item.new_count
    }));

    if (isTransposed) {
      const headers = Object.keys(dataToExport[0]);
      const transposedData = [
        headers,
        ...dataToExport.map(row => headers.map(header => (row as any)[header]))
      ];
      const pivotedData = transposedData[0].map((_, colIndex) => transposedData.map(row => row[colIndex]));
      const ws = XLSX.utils.aoa_to_sheet(pivotedData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "NewMembers");
      XLSX.writeFile(wb, `New_Members_Count_${viewMode}_Transposed_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } else {
      const ws = XLSX.utils.json_to_sheet(dataToExport);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "NewMembers");
      XLSX.writeFile(wb, `New_Members_Count_${viewMode}_${new Date().toISOString().slice(0, 10)}.xlsx`);
    }
  };

  // Fetch data
  const fetchData = async () => {
    setLoading(true);
    try {
      let url = '';
      let res = null;

      if (props.MeChartSequence?.sequenceIndex === "Action") {
        if (viewMode === "monthly") {
          // Force full year range: Jan 1 to Dec 31
          const startDateStr = `${year}-${month}-01 00:00:00`;
          const endDateStr = `${year}-${endMonth}-31 23:59:59`;
          res = await props.runSequence({
            sequence: "MeChart", action: "QuerySQL",
            chart: "new-user",
            startDate: startDateStr,
            endDate: endDateStr
          })
          console.log('res: ', res);
        } else { 
          // 3 Year Mode: Selected End Year - 2 to Selected End Year
          // e.g. End Year = 2025 -> Start 2023-01-01, End 2025-12-31
          const startYear = endYear - 2;
          const startDateStr = `${startYear}-01-01 00:00:00`;
          const endDateStr = `${endYear}-12-31 23:59:59`;
          res = await props.runSequence({
            sequence: "MeChart", action: "QuerySQL",
            chart: "new-user",
            startDate: startDateStr,
            endDate: endDateStr
          })
        }
        console.log('res: ', res);
      }

      if (viewMode === 'monthly') {
        // Force full year range: Jan 1 to Dec 31
        const startDateStr = `${year}-01-01 00:00:00`;
        const endDateStr = `${year}-12-31 23:59:59`;
        url = `http://localhost:3002/api/new-user-count?startDate=${startDateStr}&endDate=${endDateStr}`;
      } else {
        // 3 Year Mode: Selected End Year - 2 to Selected End Year
        // e.g. End Year = 2025 -> Start 2023-01-01, End 2025-12-31
        const startYear = endYear - 2;
        const startDateStr = `${startYear}-01-01 00:00:00`;
        const endDateStr = `${endYear}-12-31 23:59:59`;
        url = `http://localhost:3002/api/new-user-count-3year?startDate=${startDateStr}&endDate=${endDateStr}`;
      }

  
      let data = []
      // const response = await fetch(url);
      if (res?.length > 0 && res?.[0]?.items && Array.isArray(res?.[0]?.items)) {
        data = res?.[0]?.items;
      }
  
      if (Array.isArray(data)) {
        if (viewMode === 'monthly') {
          // Map month_index to Thai name
          const mapped = data.map((d: any) => ({
            ...d,
            month_label: thaiMonths[parseInt(d.month_index) - 1]
          }));
          setChartData(mapped);
        } else {
          setChartData(data);
        }
      } else {
        setChartData([]);
      }
      setLoading(false);

      const response = await fetch(url);
      const sqlData = await response.json()
      console.log('sqlData: ', sqlData);

    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchData();
  }, [year, endYear, viewMode]); // Refetch when year, endYear or viewMode changes

  // --- Vega Specs ---

  const getSpec = (): VisualizationSpec => {
    if (viewMode === 'monthly') {
      return {
        width: "container",
        height: "container",
        autosize: { type: "fit", contains: "padding" },
        data: { values: chartData },
        mark: { type: "bar", tooltip: true, color: "#F48FB1" },
        encoding: {
          [isTransposed ? 'y' : 'x']: {
            field: "month_label",
            type: "ordinal",
            sort: thaiMonths,
            axis: { title: "เดือน (Month)", labelAngle: isTransposed ? 0 : -45 }
          },
          [isTransposed ? 'x' : 'y']: {
            field: "new_count",
            type: "quantitative",
            axis: { title: "จำนวนสมาชิกใหม่" }
          },
          tooltip: [
            { field: "month_label", title: "เดือน" },
            { field: "new_count", title: "จำนวน", format: "," }
          ]
        }
      };
    } else {
      // 3 Year Spec
      return {
        width: "container",
        height: "container",
        autosize: { type: "fit", contains: "padding" },
        data: { values: chartData },
        mark: { type: "bar", tooltip: true, color: "#F48FB1", width: { band: 0.5 } },
        encoding: {
          [isTransposed ? 'y' : 'x']: {
            field: "year",
            type: "ordinal",
            axis: { title: "ปี (Year)", labelAngle: 0 }
          },
          [isTransposed ? 'x' : 'y']: {
            field: "new_count",
            type: "quantitative",
            axis: { title: "จำนวนสมาชิกใหม่" }
          },
          tooltip: [
            { field: "year", title: "ปี (ค.ศ.)" },
            { field: "new_count", title: "จำนวน", format: "," }
          ]
        }
      };
    }
  };

  return (
    <div id="card-mc-new-user">
      {/* Row 1: Title & Toggles */}
      <div className="controls-container">
        <Button.Group>
          <Button
            active={viewMode === 'monthly'}
            style={viewMode === 'monthly' ? { backgroundColor: 'var(--primary-theme-color)', color: 'white' } : {}}
            onClick={() => setViewMode('monthly')}
          >
            จำนวนสมาชิกใหม่
          </Button>
          <Button
            active={viewMode === '3year'}
            style={viewMode === '3year' ? { backgroundColor: 'var(--primary-theme-color)', color: 'white' } : {}}
            onClick={() => setViewMode('3year')}
          >
            จำนวนสมาชิกใหม่ 3 ปีย้อนหลัง
          </Button>
        </Button.Group>

        <Button.Group>
          <Button
            active={true}
            style={{ backgroundColor: 'var(--primary-theme-color)', color: 'white', cursor: 'default' }}
            icon
          >
            <Icon name='chart bar' /> Bar
          </Button>
        </Button.Group>
      </div>

      {/* Row 2: Filter and Download */}
      <div className="filter-container">
        <CardMCFilter
          mode={viewMode === 'monthly' ? 'oneYearMonth' : 'yearOnly'}
          year={viewMode === 'monthly' ? year : endYear}
          month={month}
          endYear={endYear}
          endMonth={endMonth}
          onYearChange={viewMode === 'monthly' ? setYear : setEndYear}
          onMonthChange={setMonth}
          onEndYearChange={setEndYear}
          onEndMonthChange={setEndMonth}
          onSearch={fetchData}
          onPrint={onPrint}
        >
          <Button style={{ backgroundColor: 'var(--primary-theme-color)', color: 'white' }} size='small' icon labelPosition='left' onClick={onDownload}>
            <Icon name='download' />
            Download .xlsx
          </Button>
          <Button
            style={{ backgroundColor: 'var(--primary-theme-color)', color: 'white' }}
            size='small'
            icon
            labelPosition='left'
            onClick={() => setIsTransposed(!isTransposed)}
            title="Transpose"
          >
            <Icon name='exchange' />
            Transpose
          </Button>
        </CardMCFilter>
      </div>

      {/* Row 4: Chart */}
      <div className="chart-wrapper">
        {loading ? (
          <div>Loading...</div>
        ) : (
          <CardMCVegaChart spec={getSpec()} actions={false} />
        )}
      </div>
    </div>
  );
};

export default CardMCNewUser;
